import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { useProfile } from '../../../hooks/UserHooks';
import { getCampaigns } from '../../(demand)/services/campaignApi';
import { getOrders } from '../../(demand)/services/orderApi';
import { getTags } from '../../(inventory)/services/tagApi';
import { OrderStatus } from '../../(demand)/types/order';

const quickActions = [
    { label: 'Criar Campanha', description: 'Iniciar uma nova campanha', path: '/campaign' },
    { label: 'Adicionar Site', description: 'Cadastrar um novo site', path: '/site' },
    { label: 'Ver Relatórios', description: 'Verificar analytics', path: '/campaign' },
    { label: 'Gerenciar Usuários', description: 'Configurações de usuários', path: '/user' },
];

function formatNumber(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString('pt-BR');
}

function deliveryRate(delivered: number, contracted: number): string {
    if (!contracted) return '0%';
    return `${Math.min(100, Math.round((delivered / contracted) * 100))}%`;
}

interface DashboardMetrics {
    totalCampaigns: number;
    totalTags: number;
    totalImpressions: number;
    totalDelivered: number;
    activeOrders: number;
    recentActivity: { title: string; time: string }[];
}

const Dashboard = () => {
    const { userProfile } = useProfile();
    const navigate = useNavigate();
    const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
    const [loading, setLoading] = useState(true);

    document.title = 'Dashboard | Ad Pulse';

    useEffect(() => {
        Promise.all([getCampaigns(), getOrders(), getTags()])
            .then(([campaigns, orders, tags]) => {
                const activeOrdersList = orders.filter(
                    (o) =>
                        o.status.includes(OrderStatus.DELIVERING) ||
                        o.status.includes(OrderStatus.DELIVERY_EXTENDED),
                );
                const totalImpressions = orders.reduce((sum, o) => sum + o.impressions, 0);
                const totalDelivered = orders.reduce((sum, o) => sum + (o.deliveryImpressions ?? 0), 0);
                const recentActivity = [...orders]
                    .sort(
                        (a, b) =>
                            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                    )
                    .slice(0, 5)
                    .map((o) => ({
                        title: o.campaign?.name
                            ? `Pedido criado em "${o.campaign.name}"`
                            : `Pedido #${o.id} criado`,
                        time: String(o.createdAt),
                    }));

                setMetrics({
                    totalCampaigns: campaigns.length,
                    totalTags: tags.filter((t) => t.enabled).length,
                    totalImpressions,
                    totalDelivered,
                    activeOrders: activeOrdersList.length,
                    recentActivity,
                });
            })
            .finally(() => setLoading(false));
    }, []);

    const stats = metrics
        ? [
              {
                  title: 'Pedidos em Entrega',
                  description: 'Pedidos ativos no momento',
                  value: String(metrics.activeOrders),
                  change: `${formatNumber(metrics.totalImpressions)} impressões contratadas`,
              },
              {
                  title: 'Impressões Entregues',
                  description: 'Total acumulado de entregas',
                  value: formatNumber(metrics.totalDelivered),
                  change: `${deliveryRate(metrics.totalDelivered, metrics.totalImpressions)} da meta entregue`,
              },
              {
                  title: 'Campanhas',
                  description: 'Total de campanhas cadastradas',
                  value: String(metrics.totalCampaigns),
                  change: `${metrics.totalTags} TAGs ativas`,
              },
          ]
        : [];

    return (
        <section className="px-6 py-8">
            <div className="mb-8">
                <h1 className="text-3xl font-bold mb-2">Bem-vindo ao Ad Pulse!</h1>
                <p className="text-muted-foreground">
                    Olá{userProfile?.name ? `, ${userProfile.name}` : ''}! Este é o seu painel do Ad
                    Server. Aqui você pode acompanhar campanhas, TAGs e métricas em tempo real.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading
                    ? Array.from({ length: 3 }).map((_, i) => (
                          <Card key={i}>
                              <CardHeader>
                                  <Skeleton className="h-4 w-36" />
                                  <Skeleton className="h-3 w-48 mt-1" />
                              </CardHeader>
                              <CardContent>
                                  <Skeleton className="h-8 w-20 mb-2" />
                                  <Skeleton className="h-3 w-40" />
                              </CardContent>
                          </Card>
                      ))
                    : stats.map((stat) => (
                          <Card key={stat.title}>
                              <CardHeader>
                                  <CardTitle className="text-base font-semibold leading-none tracking-tight">
                                      {stat.title}
                                  </CardTitle>
                                  <CardDescription>{stat.description}</CardDescription>
                              </CardHeader>
                              <CardContent>
                                  <div className="text-3xl font-bold">{stat.value}</div>
                                  <p className="text-sm text-muted-foreground">{stat.change}</p>
                              </CardContent>
                          </Card>
                      ))}
            </div>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base font-semibold leading-none tracking-tight">
                            Atividade Recente
                        </CardTitle>
                        <CardDescription>Últimas ações na sua conta</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="space-y-4">
                                {Array.from({ length: 3 }).map((_, i) => (
                                    <div key={i}>
                                        <Skeleton className="h-4 w-64 mb-1" />
                                        <Skeleton className="h-3 w-24" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {metrics?.recentActivity.length ? (
                                    metrics.recentActivity.map((activity, i) => (
                                        <div key={i} className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium">{activity.title}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {formatDistanceToNow(new Date(activity.time), {
                                                        addSuffix: true,
                                                        locale: ptBR,
                                                    })}
                                                </p>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-sm text-muted-foreground">
                                        Nenhuma atividade recente.
                                    </p>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base font-semibold leading-none tracking-tight">
                            Ações Rápidas
                        </CardTitle>
                        <CardDescription>Tarefas comuns que você pode querer realizar</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                            {quickActions.map((action) => (
                                <Button
                                    key={action.label}
                                    variant="outline"
                                    className="h-auto p-4 flex flex-col"
                                    onClick={() => navigate(action.path)}
                                >
                                    <span className="font-medium">{action.label}</span>
                                    <span className="text-xs text-muted-foreground mt-1">
                                        {action.description}
                                    </span>
                                </Button>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </section>
    );
};

export default Dashboard;
